import {
  CustomFieldRetrieveReadable,
  customFieldsDestroyMutation,
  customFieldsListOptions,
  customFieldsListQueryKey,
  customFieldsUndeletePartialUpdateMutation
} from '@crm/api'
import { ConfirmModal } from '@crm/components'
import { defaultFilters } from '@crm/utils'
import { TableContainer, Text } from '@shared/components'
import { useInvalidatePredicateQueries, useQueryParams } from '@shared/hooks'
import { useLocales } from '@shared/locales'
import { showApiErrors } from '@shared/utils'
import { useMutation, useQuery } from '@tanstack/react-query'
import { App, Flex, TableColumnsType, Tag } from 'antd'
import { MenuProps } from 'antd/lib'
import {
  CloseCircle,
  CloseSquare,
  Edit,
  TickCircle,
  TickSquare
} from 'iconsax-react'
import { useCallback, useState } from 'react'

import {
  CatalogActions,
  CatalogAddButton,
  CustomFieldsFilters,
  NomenclaturesCustomFieldsModal
} from '../components'

export const NomenclaturesCustomFields = () => {
  const { t, currentLang } = useLocales()

  const [isOpen, setIsOpen] = useState(false)
  const [editItem, setEditItem] = useState<number>()
  const [deleteItem, setDeleteItem] = useState<number>()
  const [unDeleteItem, setUnDeleteItem] = useState<number>()
  const params = useQueryParams()
  const { notification } = App.useApp()

  const { invalidateQueries } = useInvalidatePredicateQueries()

  const { data, isLoading } = useQuery(
    customFieldsListOptions({ query: { ...defaultFilters, ...params } })
  )

  const { mutate: unDeleteMutation } = useMutation({
    ...customFieldsUndeletePartialUpdateMutation(),
    onSuccess: () => {
      invalidateQueries([customFieldsListQueryKey()])
      setUnDeleteItem(undefined)
      notification.success({
        message: t('success.success'),
        description: t('success.success')
      })
    },
    onError: (error) => showApiErrors(error)
  })

  const { mutate: deleteMutation } = useMutation({
    ...customFieldsDestroyMutation(),
    onSuccess: () => {
      invalidateQueries([customFieldsListQueryKey()])
      setDeleteItem(undefined)
      notification.success({
        message: t('success.success'),
        description: t('success.success')
      })
    },
    onError: (error) => showApiErrors(error)
  })

  const getActionColumnItem: (
    id: number,
    isDeleted: boolean
  ) => MenuProps['items'] = useCallback(
    (id, isDeleted) => [
      {
        label: t('nomenclature.edit'),
        icon: <Edit size={20} />,
        key: 'edit',
        onClick: () => setEditItem(id)
      },
      { type: 'divider' },
      ...(isDeleted
        ? [
            {
              label: (
                <Text color="colorSuccess">{t('nomenclature.activate')}</Text>
              ),
              icon: <TickSquare color="green" size={20} />,
              key: 'delete',
              onClick: () => setUnDeleteItem(id)
            }
          ]
        : [
            {
              label: t('nomenclature.deactivate'),
              icon: <CloseSquare size={20} />,
              danger: true,
              key: 'undo_delete',
              onClick: () => setDeleteItem(id)
            }
          ])
    ],
    [t]
  )

  const columns: TableColumnsType<CustomFieldRetrieveReadable> = [
    {
      title: t('nomenclature.name'),
      dataIndex: `key_${currentLang?.value}`,
      key: 'name'
    },
    {
      title: t('nomenclature.type'),
      key: 'type',
      render: (_, { type }) => t(`nomenclature.field_type.${type}`)
    },
    {
      title: t('nomenclature.entity_type.title'),
      key: 'entity_type',
      render: (_, { entity_type }) =>
        t(`nomenclature.entity_type.${entity_type}`)
    },
    {
      title: t('nomenclature.required'),
      key: 'is_required',
      render: (_, { is_required }) => (
        <Tag color={is_required ? 'green' : 'red'}>
          {is_required ? t('buttons.yes') : t('buttons.no')}
        </Tag>
      )
    },
    {
      title: t('nomenclature.active'),
      key: 'is_deleted',
      render: (_, { deleted_at }) => (
        <Flex justify="center">
          {deleted_at ? (
            <CloseCircle size={16} color="red" />
          ) : (
            <TickCircle size={16} color="green" />
          )}
        </Flex>
      )
    },
    {
      width: 50,
      fixed: 'right',
      render: ({ id, deleted_at }) => (
        <CatalogActions options={getActionColumnItem(id, !!deleted_at)} />
      )
    }
  ]

  const onToggle = () => setIsOpen((prev) => !prev)

  const onDelete = () => {
    if (deleteItem) {
      deleteMutation({ path: { id: deleteItem } })
    }
  }

  const onUnDelete = () => {
    if (unDeleteItem) {
      unDeleteMutation({
        path: { id: unDeleteItem }
      })
    }
  }

  const onCancelDelete = () => {
    setDeleteItem(undefined)
    setUnDeleteItem(undefined)
  }

  const onCloseModal = () => {
    setIsOpen(false)
    setEditItem(undefined)
  }

  return (
    <>
      <TableContainer
        loading={isLoading}
        data={data}
        columns={columns}
        leftChildren={<CustomFieldsFilters />}
        rightChildren={
          <CatalogAddButton onClick={onToggle}>
            {t('nomenclature.add_custom_field')}
          </CatalogAddButton>
        }
        bordered
      />

      <ConfirmModal
        open={!!deleteItem}
        onSuccess={onDelete}
        onCancel={onCancelDelete}
      />

      <ConfirmModal
        open={!!unDeleteItem}
        onSuccess={onUnDelete}
        onCancel={onCancelDelete}
        children={t('nomenclature.activateConfirm')}
      />

      {(isOpen || editItem) && (
        <NomenclaturesCustomFieldsModal onCancel={onCloseModal} id={editItem} />
      )}
    </>
  )
}
